import React, { useState, useMemo } from 'react';
import { PLATFORM_MARKETPLACE_FEE_PER_SIDE } from '../../config/constants';
import { formatExactShareMoney, formatUsd, estimateProRataManagementFeeUsd } from '../../data/countries';
import {
  getInvestmentLots,
  getTotalSharesForLots,
  getWeightedCostPerShareUsd,
  getWeightedSubtotalPerShareUsd,
  getAggregatedPrimaryReturnBreakdown,
  isMarketplaceAcquiredLot,
  shouldReturnSharesToPrimaryOffering,
} from '../../utils/investments';

const SellModal = ({ investment, property, onClose, onConfirm, country }) => {
  const lots = useMemo(() => getInvestmentLots(investment), [investment]);
  const maxShares = getTotalSharesForLots(lots);
  const [shares, setShares] = useState(maxShares > 0 ? 1 : 0);
  const [sharesInput, setSharesInput] = useState(String(maxShares > 0 ? 1 : 0));

  if (!lots.length || maxShares <= 0) return null;

  const isPrimaryFunding = shouldReturnSharesToPrimaryOffering(property, lots);
  const costPerShare = getWeightedCostPerShareUsd(lots);
  const subtotalPerShare = getWeightedSubtotalPerShareUsd(lots);
  const primaryBreakdown = getAggregatedPrimaryReturnBreakdown(lots, shares);
  let mgmtFee = 0;
  if (isPrimaryFunding) {
    let remainingForFee = shares;
    for (const lot of lots) {
      if (remainingForFee <= 0) break;
      const fromLot = Math.min(remainingForFee, Number(lot.shares) || 0);
      if (fromLot <= 0) continue;
      mgmtFee += estimateProRataManagementFeeUsd(lot, fromLot);
      remainingForFee -= fromLot;
    }
    mgmtFee = Math.round(mgmtFee * 100) / 100;
  }
  const netPrimary = Math.round((primaryBreakdown.subtotalRefundUsd - mgmtFee) * 100) / 100;
  const gross = shares * costPerShare;
  const marketplaceBuyerFee = Math.round(gross * PLATFORM_MARKETPLACE_FEE_PER_SIDE * 100) / 100;
  const marketplaceSellerFee = marketplaceBuyerFee;
  const marketplaceSellerNet = Math.round((gross - marketplaceSellerFee) * 100) / 100;
  const lotCount = lots.length;

  const clampShares = (n) => Math.min(maxShares, Math.max(1, n));

  const applyShareCount = (raw) => {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
      setSharesInput('');
      return;
    }
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n)) return;
    const next = clampShares(n);
    setShares(next);
    setSharesInput(String(next));
  };

  const commitShareInput = () => {
    const trimmed = sharesInput.trim();
    if (!trimmed) {
      setShares(1);
      setSharesInput('1');
      return;
    }
    const n = parseInt(trimmed, 10);
    const next = Number.isFinite(n) ? clampShares(n) : 1;
    setShares(next);
    setSharesInput(String(next));
  };

  const getEffectiveShares = () => {
    const trimmed = sharesInput.trim();
    const n = parseInt(trimmed, 10);
    return Number.isFinite(n) ? clampShares(n) : 1;
  };

  return (
    <div className="modal-overlay active" onClick={(e)=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{isPrimaryFunding ? "Return Shares" : "List on Marketplace"} — {property.title}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <p style={{ color: "var(--text-light)", fontSize: "0.9rem", marginBottom: "1rem" }}>
            {isPrimaryFunding
              ? "This property is still in primary funding. Your shares go back to the offering for other investors to buy. Only the share purchase price is refunded; acquisition fee and VAT are non-refundable."
              : lots.some(isMarketplaceAcquiredLot)
                ? "These shares were bought on the secondary marketplace. Listing them for sale lets another investor purchase them. You are paid when they buy (1% seller fee applies)."
                : "This property is fully funded. Shares are listed on the secondary marketplace until another investor buys them."}
          </p>
          {lotCount > 1 ? (
            <p style={{ color: "var(--text-light)", fontSize: "0.85rem", marginBottom: "1rem" }}>
              You have {lotCount} separate purchase{lotCount !== 1 ? "s" : ""} for this property. Selling {shares} share{shares !== 1 ? "s" : ""} will use your lots in order.
            </p>
          ) : null}
          <div className="investment-summary">
            <div className="summary-row"><span>Your Shares</span><span>{maxShares}</span></div>
            <div className="summary-row"><span>Share price (subtotal)</span><span>{formatExactShareMoney(subtotalPerShare, country)}</span></div>
            <div className="summary-row"><span>Total paid / share (incl. fees)</span><span>{formatExactShareMoney(costPerShare, country)}</span></div>
            <div className="summary-row"><span>Shares to {isPrimaryFunding ? "return" : "list"}</span><span>{shares}</span></div>
            {isPrimaryFunding ? (
              <>
                <div className="summary-row"><span>Share subtotal refund</span><span>{formatUsd(primaryBreakdown.subtotalRefundUsd)}</span></div>
                <div className="summary-row" style={{ fontSize: "0.88rem", color: "var(--text-light)" }}>
                  <span>Acquisition fee (retained)</span>
                  <span>{formatUsd(primaryBreakdown.acquisitionFeeRetainedUsd)}</span>
                </div>
                <div className="summary-row" style={{ fontSize: "0.88rem", color: "var(--text-light)" }}>
                  <span>VAT (retained)</span>
                  <span>{formatUsd(primaryBreakdown.vatRetainedUsd)}</span>
                </div>
                <div className="summary-row"><span>Management fee (pro-rated)</span><span>−{formatUsd(mgmtFee)}</span></div>
                <div className="summary-row" style={{ fontWeight: 700 }}><span>Credited to wallet</span><span>{formatUsd(netPrimary)}</span></div>
              </>
            ) : (
              <>
                <div className="summary-row"><span>List price / share</span><span>{formatExactShareMoney(costPerShare, country)}</span></div>
                <div className="summary-row"><span>Platform fee (1% seller)</span><span>−{formatUsd(marketplaceSellerFee)}</span></div>
                <div className="summary-row"><span>When sold — you receive</span><span>{formatUsd(marketplaceSellerNet)}</span></div>
                <div className="summary-row" style={{ fontSize: "0.85rem", color: "var(--text-light)" }}><span>Buyer pays (incl. 1% fee)</span><span>{formatUsd(gross + marketplaceBuyerFee)}</span></div>
              </>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="sell-shares-input">Shares</label>
            <div className="sell-shares-controls">
              <input
                id="sell-shares-input"
                type="number"
                min={1}
                max={maxShares}
                step={1}
                value={sharesInput}
                onChange={(e) => applyShareCount(e.target.value)}
                onBlur={commitShareInput}
                aria-label="Number of shares to sell"
              />
            </div>
          </div>
          <button className="btn btn-accent" style={{width:"100%",padding:"1rem"}} onClick={() => onConfirm(getEffectiveShares())}>
            {isPrimaryFunding ? "Return shares to offering" : "List on marketplace"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SellModal;
